import { BaseNode, type NodeConfig } from 'mflows';

interface FetchRequestConfig extends NodeConfig {
  url: string;
  method: string; // get/post/put/delete/patch...
  headers?: Record<string, string>;
  body?: any;
  timeout?: number; // ms
}

interface FetchResponseData {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: any;
  config: {
    url: string;
    method: string;
    headers?: Record<string, string>;
    timeout?: number;
    body?: any;
  };
}

interface FetchPayload {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  timeout?: number;
  value?: any;
}

export class FetchRequest extends BaseNode<FetchRequestConfig, FetchResponseData> {
  static NodeType = 'Fetch请求';

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', name: '执行', allowTypes: ['flow'], dataType: 'flow' },
      { id: 'payload', name: '输入', allowTypes: ['object','customFunction'], dataType: 'object' },
      { id: 'http-headers', name: '请求头', allowTypes: ['object'], dataType: 'object' },
      { id: 'http-data', name: '请求体', allowTypes: ['object'], dataType: 'object' }
    ]);

    this.setOutputPorts([
      { id: 'http-response', name: '响应', dataType: 'object' },
      { id: 'http-error', name: '错误', dataType: 'object' },
      { id: 'next', name: '下一步', dataType: 'flow' }
    ]);
  }

  protected receiveData(pointId: string, data: unknown): void {
    // 根据不同的输入点更新配置（仅做数据传递与配置合并）
    switch (pointId) {
      case 'payload':
        if (typeof data === 'object' && data !== null) {
          const payload = data as FetchPayload;
          const updates: Partial<FetchRequestConfig> = {};
          if (payload.url && !this.settings.url) updates.url = payload.url;
          if (payload.method && !this.settings.method) updates.method = payload.method;
          if (payload.headers && Object.keys(payload.headers).length > 0) {
            updates.headers = { ...this.settings.headers, ...payload.headers };
          }
          if (payload.timeout && !this.settings.timeout) updates.timeout = payload.timeout;
          if (payload.value !== undefined) updates.body = payload.value;
          if (Object.keys(updates).length > 0) this.updateSettings(updates);
        }
        break;
      case 'http-headers':
        if (typeof data === 'object' && data !== null) {
          const headers = { ...this.settings.headers, ...(data as Record<string, string>) };
          this.updateSettings({ headers });
        }
        break;
      case 'http-data':
        if (data !== null) this.updateSettings({ body: data });
        break;
      default:
        break;
    }

    const inputPoint = this.findInputPort(pointId);
    if (inputPoint?.dataType === 'flow') {
      this.executeConfigUpdate();
    }
  }

  protected checkConfig(): boolean {
    return Boolean(this.settings.url) && Boolean(this.settings.method);
  }

  protected async processConfigUpdate(settings: FetchRequestConfig): Promise<any> {
    const controller = new AbortController();
    const timeout = settings.timeout ?? 10000;
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const method = (settings.method || 'get').toUpperCase();
      const headers: Record<string, string> = { ...(settings.headers || {}) };

      const init: RequestInit = {
        method,
        headers,
        signal: controller.signal
      };

      let url = settings.url;
      if (method === 'GET') {
        // 将 body 作为查询参数
        if (settings.body && typeof settings.body === 'object') {
          const searchParams = new URLSearchParams();
          Object.entries(settings.body).forEach(([k, v]) => {
            if (v === undefined || v === null) return;
            searchParams.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
          });
          const qs = searchParams.toString();
          if (qs) url += (url.includes('?') ? '&' : '?') + qs;
        }
      } else {
        // 非 GET：将 body 序列化为 JSON
        if (settings.body !== undefined) {
          if (!headers['Content-Type'] && !headers['content-type']) {
            headers['Content-Type'] = 'application/json';
          }
          init.body = typeof settings.body === 'string' ? settings.body : JSON.stringify(settings.body);
          init.headers = headers;
        }
      }

      const resp = await fetch(url, init);
      const contentType = resp.headers.get('content-type') || '';
      let data: any;
      try {
        if (contentType.includes('application/json')) {
          data = await resp.json();
        } else if (contentType.includes('text/')) {
          data = await resp.text();
        } else {
          // 尝试文本，失败再用 blob
          data = await resp.text();
        }
      } catch {
        try {
          data = await resp.text();
        } catch {
          data = null;
        }
      }

      const headersObj: Record<string, string> = {};
      resp.headers.forEach((v, k) => { headersObj[k] = v; });

      const responseData: FetchResponseData = {
        status: resp.status,
        statusText: resp.statusText,
        headers: headersObj,
        data,
        config: {
          url: settings.url,
          method: settings.method,
          headers: settings.headers,
          timeout: settings.timeout,
          body: settings.body
        }
      };

      Object.assign(this.nodeData, { value: responseData });
      return this.nodeData;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return {
        value: { message: err.message },
        error: err,
        state: 'error',
        updateTime: Date.now()
      };
    } finally {
      clearTimeout(id);
    }
  }

  protected getOutputPortActions(): { dataOnly?: string[]; triggerFlow?: string[] } {
    if (this.getState() === 'error') {
      return { dataOnly: ['http-error'], triggerFlow: [] };
    }
    return { dataOnly: ['http-response'], triggerFlow: ['next'] };
  }
}